/**
 * Trainova AI — Multilingual & Internationalization Context Provider
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  LanguageContextValue,
  LanguageMeta,
  SYSTEM_LANGUAGES,
} from './types';
import { LOCALES } from './locales';
import { useAuth } from '../stores/authStore';
import { languages as languagesApi } from '../services/api';

const STORAGE_KEY = 'trainova_user_language';
const SESSION_LEGACY_KEY = 'trainova_preferred_language';

const LanguageContext = createContext<LanguageContextValue | null>(null);

// In-memory client cache for dynamic translations
const dynamicTranslationCache = new Map<string, string>();

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user, updateUserLanguage } = useAuth();

  // Storage / URL initial seed
  const getInitialLanguage = (): string => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const found = SYSTEM_LANGUAGES.find(
          (l) =>
            l.code.toLowerCase() === stored.toLowerCase() ||
            l.code.replace('-', '_').toLowerCase() === stored.replace('-', '_').toLowerCase()
        );
        if (found) return found.code;
      }
      const legacy = sessionStorage.getItem(SESSION_LEGACY_KEY);
      if (legacy) {
        const found = SYSTEM_LANGUAGES.find(
          (l) =>
            l.code.toLowerCase() === legacy.toLowerCase() ||
            l.code.replace('-', '_').toLowerCase() === legacy.replace('-', '_').toLowerCase()
        );
        if (found) return found.code;
      }
    } catch {
      // Fallback
    }
    return user?.preferredLanguage || 'en';
  };

  const [currentLanguage, setCurrentLanguageState] = useState<string>(getInitialLanguage);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([
    'en',
    'en-GB',
    'es',
    'fr',
    'de',
    'hi',
    'ta',
    'te',
    'kn',
    'mr',
    'bn',
    'ar',
    'pt',
    'ja',
    'zh',
  ]);
  const [tenantDefaultLanguage, setTenantDefaultLanguage] = useState<string>('en');
  const [fallbackLanguage, setFallbackLanguage] = useState<string>('en');
  const [allowUserLanguageSelection, setAllowUserLanguageSelection] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Sync with user's preferred language from auth when loaded
  useEffect(() => {
    if (user?.preferredLanguage && user.preferredLanguage !== currentLanguage) {
      const stored = localStorage.getItem(STORAGE_KEY);
      // If user hasn't explicitly set a local override during this session, adopt user profile preference
      if (!stored || stored === user.preferredLanguage) {
        setCurrentLanguageState(user.preferredLanguage);
        sessionStorage.setItem(SESSION_LEGACY_KEY, user.preferredLanguage);
      }
    }
  }, [user?.preferredLanguage]);

  // Fetch tenant language governance policies
  useEffect(() => {
    let isMounted = true;
    const fetchTenantLanguages = async () => {
      try {
        const res = await languagesApi.getTenantLanguages();
        if (isMounted && res.success && res.data) {
          const {
            supportedLanguages: tenantSupported,
            defaultLanguage,
            fallbackLanguage: tenantFallback,
            allowUserLanguageSelection: tenantAllow,
          } = res.data;

          if (tenantSupported && Array.isArray(tenantSupported) && tenantSupported.length > 0) {
            setSupportedLanguages(tenantSupported);
          }
          if (defaultLanguage) setTenantDefaultLanguage(defaultLanguage);
          if (tenantFallback) setFallbackLanguage(tenantFallback);
          if (typeof tenantAllow === 'boolean') setAllowUserLanguageSelection(tenantAllow);

          // If current language is not in tenant-supported list and user cannot switch, enforce default
          const active = localStorage.getItem(STORAGE_KEY) || currentLanguage;
          if (
            tenantSupported &&
            tenantSupported.length > 0 &&
            !tenantSupported.includes(active) &&
            !tenantAllow
          ) {
            const fallback = defaultLanguage || 'en';
            setCurrentLanguageState(fallback);
            localStorage.setItem(STORAGE_KEY, fallback);
            sessionStorage.setItem(SESSION_LEGACY_KEY, fallback);
          }
        }
      } catch (e) {
        // Fall back gracefully to defaults
      }
    };

    fetchTenantLanguages();
    return () => {
      isMounted = false;
    };
  }, [user?.tenantId]);

  // Compute available languages meta list
  const availableLanguages = useMemo<LanguageMeta[]>(() => {
    return SYSTEM_LANGUAGES.filter((meta) => supportedLanguages.includes(meta.code));
  }, [supportedLanguages]);

  // Check RTL direction
  const isRTL = useMemo<boolean>(() => {
    const meta = SYSTEM_LANGUAGES.find((l) => l.code === currentLanguage);
    return Boolean(meta?.isRtl);
  }, [currentLanguage]);

  // Synchronize document attributes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = currentLanguage;
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    }
  }, [currentLanguage, isRTL]);

  // Set language action
  const setLanguage = useCallback(
    async (newLang: string) => {
      // Find matching language case-insensitively
      const matched = SYSTEM_LANGUAGES.find(
        (l) =>
          l.code.toLowerCase() === newLang.toLowerCase() ||
          l.code.replace('-', '_').toLowerCase() === newLang.replace('-', '_').toLowerCase()
      );
      if (!matched) return;
      const targetCode = matched.code;

      // Update state immediately for instant responsive UI
      setCurrentLanguageState(targetCode);

      // Persist locally
      try {
        localStorage.setItem(STORAGE_KEY, targetCode);
        sessionStorage.setItem(SESSION_LEGACY_KEY, targetCode);
      } catch {
        // Ignore storage errors in private mode
      }

      // Update auth context
      updateUserLanguage(targetCode);

      // If user is logged in, persist to backend profile
      if (user?.userId) {
        try {
          await languagesApi.updateMyLanguage(targetCode);
        } catch {
          // Non-blocking background sync failure
        }
      }
    },
    [user?.userId, updateUserLanguage]
  );

  // String interpolation & translation function
  const t = useCallback(
    (key: string, params?: Record<string, string | number>, defaultText?: string): string => {
      const activeDict =
        LOCALES[currentLanguage] ||
        LOCALES[currentLanguage.toLowerCase()] ||
        LOCALES[currentLanguage.replace('-', '_')] ||
        LOCALES['en'];
      const fallbackDict =
        LOCALES[fallbackLanguage] ||
        LOCALES[fallbackLanguage.toLowerCase()] ||
        LOCALES['en'];
      const enDict = LOCALES['en'];

      let template =
        activeDict?.[key] ??
        fallbackDict?.[key] ??
        enDict?.[key] ??
        defaultText ??
        key;

      if (params && typeof template === 'string') {
        Object.entries(params).forEach(([paramKey, val]) => {
          const stringVal = String(val ?? '');
          // Replace {{key}}
          template = template.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), stringVal);
          // Replace {key}
          template = template.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), stringVal);
        });
      }

      return template;
    },
    [currentLanguage, fallbackLanguage]
  );

  // Dynamic on-demand translation using Google Cloud Translation API backend
  const translateDynamic = useCallback(
    async (text: string, options?: { context?: string }): Promise<string> => {
      if (!text || !text.trim() || currentLanguage === 'en' || currentLanguage === 'en-GB' || currentLanguage.startsWith('en')) {
        return text;
      }

      const cacheKey = `${currentLanguage}:${options?.context || 'general'}:${text}`;
      if (dynamicTranslationCache.has(cacheKey)) {
        return dynamicTranslationCache.get(cacheKey)!;
      }

      try {
        setIsLoading(true);
        const res = await languagesApi.translate({
          text,
          sourceLang: 'en',
          targetLang: currentLanguage,
          context: options?.context || 'general',
        });

        if (res.success && res.data?.translatedText) {
          const translated = res.data.translatedText;
          dynamicTranslationCache.set(cacheKey, translated);
          return translated;
        }
      } catch (err) {
        console.warn('[LanguageContext] Dynamic translation failed, using source:', err);
      } finally {
        setIsLoading(false);
      }

      return text;
    },
    [currentLanguage]
  );

  const value = useMemo<LanguageContextValue>(() => {
    return {
      currentLanguage,
      setLanguage,
      supportedLanguages,
      availableLanguages,
      tenantDefaultLanguage,
      fallbackLanguage,
      allowUserLanguageSelection,
      isRTL,
      isLoading,
      t,
      translateDynamic,
    };
  }, [
    currentLanguage,
    setLanguage,
    supportedLanguages,
    availableLanguages,
    tenantDefaultLanguage,
    fallbackLanguage,
    allowUserLanguageSelection,
    isRTL,
    isLoading,
    t,
    translateDynamic,
  ]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
